import * as THREE from 'three';
import {store} from 'store/customer';
import {
    CabinetMesh,
    RoomPlannerProductWithTemplate,
    UndoActionContext,
} from 'components/customer/RoomPlanner/types';
import {ComputedDimension} from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import {
    autoSave,
    debouncedAutoSave,
} from 'components/customer/RoomPlanner/lib/useAutoSave';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {autoRotateClamping} from 'components/customer/RoomPlanner/helpers/boundaries/autoRotate';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';
import {reflushAfterRotation} from 'components/customer/RoomPlanner/helpers/reflushAfterRotation';
import {
    getRoomWallGroup,
    resolveLiveWall,
    settleShapeAfterRotation,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {isNonRectShapeProduct} from 'components/customer/RoomPlanner/helpers/shapeProducts';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {tryPlaceDuplicateClone} from 'components/customer/RoomPlanner/helpers/cabinetPlacement';
import {roomPlannerApi} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {
    notifyFromRoomPlanner,
    ROOM_PLANNER_MSG_DUPLICATE_NO_SPACE,
} from 'components/customer/RoomPlanner/lib/useRoomPlannerNotification';
import {
    duplicateIdsSet,
    getProducts,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {
    invalidateParticularRoomAndCost,
    ProductApi,
} from 'components/customer/Product/store/productApi';
import {RECENTLY_ADDED_KEYS, addRecentItem} from 'hooks/RecentlyAdded';
import {dispatchCabinetAddToHistory} from 'components/customer/RoomPlanner/helpers/history/cabinetAddAction';
import {formatHistoryTitlePrefix} from 'components/customer/RoomPlanner/helpers/history/formatHistoryTitle';
import {
    setRotating,
    parseJobCabinetIdFromMesh,
    triggerIfWireframeMode,
} from './cabinetGroupUtils';
import {DEFAULT_POSITION_Y} from '../renderCabinetGroup';

interface AssignMethodsParams {
    cabinetGroup: CabinetMesh;
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>;
    dimensions: ComputedDimension;
    room: string;
    roomType: string | undefined;
    isUpperProduct: boolean;
    isManualPanelProduct: boolean;
    sitOnFloor: boolean;
    renderCabinetGroup: (
        product: RoomPlannerProductWithTemplate,
        dimensions: ComputedDimension,
        room: string,
        cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
        row?: number,
        tempMesh?: boolean
    ) => {widthIncrement: number; cabinetGroup: CabinetMesh};
}

/**
 * Creates the rotateAroundCenter function for rotating cabinets
 * @param {CabinetMesh} cabinetGroup - Cabinet group mesh
 * @param {boolean} isUpperProduct - Whether cabinet is an upper product
 * @param {boolean} isManualPanelProduct - Whether cabinet is a manual panel product
 * @param {string | undefined} roomType - Type of room (RECTANGULAR, LSHAPED, etc.)
 * @return {Function} Function that rotates cabinet around center point
 */
const createRotateAroundCenter = (
    cabinetGroup: CabinetMesh,
    isUpperProduct: boolean,
    isManualPanelProduct: boolean,
    roomType: string | undefined
) => {
    return (angle: number) => {
        const preBox = new THREE.Box3().setFromObject(cabinetGroup);
        const oldCenter = preBox.getCenter(new THREE.Vector3());

        // currentWall can be stale (see resolveLiveWall); re-validate before
        // trusting it below.
        const currentWall = resolveLiveWall(
            cabinetGroup,
            cabinetGroup.userData.currentWall,
            getRoomWallGroup(cabinetGroup, Camera.scene)
        );
        cabinetGroup.userData.currentWall = currentWall;

        // Manual panels: rotate around Z-axis in local space
        // After X rotation of -90°, local Z-axis points up (world Y), giving compass rotation
        // This keeps the panel flat on the floor while rotating
        // Other cabinets: rotate around Z-axis normally
        cabinetGroup.rotation.z += angle;

        cabinetGroup.updateMatrixWorld();

        const newBox = new THREE.Box3().setFromObject(cabinetGroup);
        const newCenter = newBox.getCenter(new THREE.Vector3());

        const offset = newCenter.clone().sub(oldCenter);
        cabinetGroup.position.sub(offset);
        cabinetGroup.updateMatrixWorld();

        if (currentWall) {
            // Re-flush against the wall (or cabinet-snap target's bound) using
            // pure geometry; preserves the same signed gap that existed
            // before rotation, so the cabinet stays flush in both rectangular
            // and L-shape rooms without per-direction case tables.
            const reflushed = reflushAfterRotation(
                cabinetGroup,
                currentWall,
                preBox
            );
            if (reflushed) {
                cabinetGroup.position.copy(reflushed);
                cabinetGroup.updateMatrixWorld();
            }
        } else {
            const {clamped} = autoRotateClamping(
                cabinetGroup,
                null,
                cabinetGroup.position,
                isUpperProduct,
                roomType ?? 'RECTANGULAR'
            );
            if (clamped) {
                cabinetGroup.position.copy(clamped);
                cabinetGroup.updateMatrixWorld();
            }
        }

        // reflushAfterRotation only bounds the axes of the wall it is handed:
        // the normal axis by the preserved gap, the parallel axis by that
        // wall's own AABB span. In an L room those spans reach the far faces of
        // the perpendicular walls, and no single wall describes the cut-out, so
        // a rotated shape can land inside a wall or in the notch. Shapes only,
        // matching the drag path in useProductEvents onMouseMove: other
        // cabinets may legitimately sit in a wall.
        if (isNonRectShapeProduct(cabinetGroup)) {
            settleShapeAfterRotation(
                cabinetGroup,
                getRoomWallGroup(cabinetGroup, Camera.scene)
            );
        }
    };
};

/**
 * Assigns all cabinet methods to the userData object
 */
export const assignCabinetMethods = ({
    cabinetGroup,
    cabinetGroupListRef,
    dimensions,
    room,
    roomType,
    isUpperProduct,
    isManualPanelProduct,
    sitOnFloor,
    renderCabinetGroup,
}: AssignMethodsParams): void => {
    const {scale, wallHeight} = dimensions;
    const directions = ['front', 'left', 'back', 'right'];

    const rotateAroundCenter = createRotateAroundCenter(
        cabinetGroup,
        isUpperProduct,
        isManualPanelProduct,
        roomType
    );

    cabinetGroup.userData.rotateLeft = () => {
        setRotating();

        // Store the current direction before rotation
        const originalDirection = cabinetGroup.userData.currentDirection;
        cabinetGroup.userData.currentRotation = cabinetGroup.rotation;
        // Update direction
        const currentIndex = directions.indexOf(originalDirection);
        cabinetGroup.userData.currentDirection =
            directions[(currentIndex + 1) % 4];
        rotateAroundCenter(Math.PI / 2);

        productEvents.revalidateOverlaps();

        debouncedAutoSave(cabinetGroup);
    };

    cabinetGroup.userData.rotateRight = () => {
        setRotating();

        // Store the current direction before rotation
        const originalDirection = cabinetGroup.userData.currentDirection;
        cabinetGroup.userData.currentRotation = cabinetGroup.rotation;
        // Update direction
        const currentIndex = directions.indexOf(originalDirection);
        cabinetGroup.userData.currentDirection =
            directions[(currentIndex + 3) % 4]; // +3 is the same as -1
        rotateAroundCenter(-Math.PI / 2);

        productEvents.revalidateOverlaps();
        debouncedAutoSave(cabinetGroup);
    };

    // Duplicate: placement in `tryPlaceDuplicateClone` (cabinetPlacement); persist via API, wire ids + history.
    cabinetGroup.userData.duplicateCabinet = async (
        ignoreHistoryDispatch = false,
        ctx?: UndoActionContext
    ) => {
        let placedMesh: CabinetMesh | null = null;
        try {
            const numericRoomId = Number(cabinetGroup.userData.values.room_id);
            const resolvedRoomType = roomType || 'RECTANGULAR';
            const jobCabinetId = parseJobCabinetIdFromMesh(
                cabinetGroup.userData.id
            );
            const roomCabinetNumber = cabinetGroupListRef.current.length + 1;

            placedMesh = tryPlaceDuplicateClone(
                cabinetGroup,
                dimensions,
                room,
                cabinetGroupListRef,
                resolvedRoomType,
                roomCabinetNumber,
                0,
                numericRoomId,
                renderCabinetGroup
            );

            if (!placedMesh) {
                notifyFromRoomPlanner({
                    message: ROOM_PLANNER_MSG_DUPLICATE_NO_SPACE,
                    variant: 'danger',
                });
                return;
            }

            triggerIfWireframeMode(placedMesh);

            const response = await store.dispatch(
                roomPlannerApi.endpoints.duplicateProduct.initiate({
                    roomId: numericRoomId,
                    jobCabinetId,
                })
            );

            if (!response?.data) {
                deleteCabinetMesh(placedMesh, false, cabinetGroupListRef);
                return;
            }

            const {job_cabinet_id: newId, type: cabinetType} = response.data;
            const meshIndex = 0;

            placedMesh.userData.id = `${newId}-${meshIndex}`;
            placedMesh.userData.values.job_cabinet_id = newId;
            placedMesh.userData.values.id = newId;
            placedMesh.userData.values.duplicate_id = `${newId}-${meshIndex}`;
            placedMesh.userData.values.cabinet_quantity = 1;
            autoSave(placedMesh, true);

            ctx?.setRestoredCabinetId?.(newId);

            await store.dispatch(
                ProductApi.endpoints.updateProductQuantity.initiate({
                    cabinetId: newId,
                    quantity: 1,
                })
            );

            store.dispatch(duplicateIdsSet(newId, [meshIndex]));
            store.dispatch(invalidateTotalProductCount());
            store.dispatch(invalidateParticularRoomAndCost(numericRoomId));
            addRecentItem(cabinetType, RECENTLY_ADDED_KEYS.PRODUCT);

            if (!ignoreHistoryDispatch) {
                const state = store.getState();
                const roomIndex = state.roomPlanner.roomIndex;
                const roomPlannerProducts = getProducts(state) ?? [];
                const roomCabNumber = roomPlannerProducts.length + 1;
                const typeName = cabinetGroup.userData.values?.type_name ?? '';
                dispatchCabinetAddToHistory(store.dispatch, {
                    title: `Add ${formatHistoryTitlePrefix(roomIndex, roomCabNumber)} ${typeName} From Duplicate`.trimEnd(),
                    data: {
                        id: newId,
                        redoAction: (historyCtx) =>
                            void cabinetGroup.userData.duplicateCabinet(
                                true,
                                historyCtx
                            ),
                    },
                });
            }
        } catch {
            if (placedMesh) {
                deleteCabinetMesh(placedMesh, false, cabinetGroupListRef);
            }
        }
    };

    // TODO: Temporarily disabled - may be re-enabled in the future
    // These functions are preserved but not currently used in the UI
    cabinetGroup.userData.moveUp = () => {
        try {
            setRotating();
            productEvents.revalidateOverlaps();

            // Get cabinet dimensions
            const {height} = getGroupWidth(cabinetGroup);

            // Calculate the upper boundary limit (top of the wall - half the cabinet height)
            const upperLimit = wallHeight / 2 - (height * scale) / 2 - 25;

            // Calculate the new position
            const newY = cabinetGroup.position.y + 10;

            // Only move if it won't exceed the wall boundary
            if (newY <= upperLimit) {
                cabinetGroup.position.y = newY;
                // Save the updated position
                debouncedAutoSave(cabinetGroup);
            } else {
                // Optionally move to the exact upper limit if we're trying to go beyond
                cabinetGroup.position.y = upperLimit;
                debouncedAutoSave(cabinetGroup);
            }
        } catch (error) {}
    };

    cabinetGroup.userData.moveDown = () => {
        try {
            setRotating();
            productEvents.revalidateOverlaps();

            // Get cabinet dimensions
            const {height} = getGroupWidth(cabinetGroup);

            // Calculate the lower boundary limit (bottom of the wall + half the cabinet height)
            const lowerLimit = -(wallHeight / 2) + (height * scale) / 2 + 64;

            // Calculate the new position
            const newY = cabinetGroup.position.y - 10;

            // Only move if it won't exceed the wall boundary
            if (newY >= lowerLimit) {
                cabinetGroup.position.y = newY;

                // Save the updated position
                debouncedAutoSave(cabinetGroup);
            } else {
                // Optionally move to the exact lower limit if we're trying to go beyond
                cabinetGroup.position.y = lowerLimit;
                debouncedAutoSave(cabinetGroup);
            }
        } catch {}
    };

    cabinetGroup.userData.move = (postion: THREE.Vector3) => {
        try {
            setRotating();

            cabinetGroup.position.x = postion.x;
            cabinetGroup.position.y = postion.y;
            cabinetGroup.position.z = postion.z;
            debouncedAutoSave(cabinetGroup);
        } catch (error) {}
    };

    cabinetGroup.userData.deleteCabinet = () =>
        deleteCabinetMesh(cabinetGroup, false, cabinetGroupListRef);

    cabinetGroup.userData.adaptToWallHeightChange = (
        newWallHeight: number,
        shouldSave = false
    ) => {
        const positionY =
            -(newWallHeight / 2) +
            (isUpperProduct
                ? DEFAULT_POSITION_Y.isUpperProduct
                : sitOnFloor
                  ? DEFAULT_POSITION_Y.sitOnFloor
                  : 73);

        cabinetGroup.position.y = positionY;
        cabinetGroup.userData.wallHeight = newWallHeight;

        if (shouldSave) {
            autoSave(cabinetGroup);
        }
    };
};
